import type { ThemeConfig } from "antd";

export type ThemeMode = "light" | "dark";

const THEME_FONT =
  '"Inter", "HarmonyOS Sans", "PingFang SC", "Microsoft YaHei", sans-serif';

const sharedTokens = {
  colorPrimary: "#1890ff",
  colorSuccess: "#52c41a",
  colorWarning: "#faad14",
  colorError: "#ff4d4f",
  colorInfo: "#1890ff",
  borderRadius: 8,
  fontSize: 14,
  controlHeight: 40,
  fontFamily: THEME_FONT,
};

const modeTokens: Record<ThemeMode, Partial<ThemeConfig["token"]>> = {
  light: {
    colorBgBase: "#f5f8ff",
    colorBgContainer: "#ffffff",
    colorTextBase: "#0b1f4b",
    colorText: "#102a57",
    colorTextSecondary: "#6b7a99",
    boxShadowSecondary: "0 12px 30px rgba(15, 23, 42, 0.08)",
  },
  dark: {
    colorBgBase: "#050e1f",
    colorBgContainer: "#0a1b36",
    colorTextBase: "#f4f7ff",
    colorText: "#d3ddff",
    colorTextSecondary: "#8fa5d8",
    boxShadowSecondary: "0 20px 40px rgba(6, 10, 25, 0.45)",
  },
};

const baseComponents: ThemeConfig["components"] = {
  Button: {
    borderRadius: 999,
    controlHeight: 40,
  },
  Tag: {
    borderRadius: 12,
  },
  Input: {
    borderRadius: 10,
  },
};

const modeComponents: Record<ThemeMode, ThemeConfig["components"]> = {
  light: {
    Layout: {
      headerBg: "#ffffff",
      headerColor: "#102a57",
      siderBg: "#0b1f4b",
      bodyBg: "#f5f8ff",
      triggerBg: "#d9e8ff",
      triggerColor: "#102a57",
    },
    Menu: {
      itemColor: "rgba(255,255,255,0.7)",
      itemSelectedColor: "#ffffff",
      itemSelectedBg: "rgba(24, 144, 255, 0.35)",
      itemHoverColor: "#ffffff",
    },
    Card: {
      colorBgContainer: "#ffffff",
      borderRadiusLG: 16,
      paddingLG: 20,
    },
    Table: {
      borderColor: "rgba(15, 23, 42, 0.05)",
      headerBg: "#eef4ff",
      headerColor: "#4b5c7a",
      rowHoverBg: "rgba(24,144,255,0.08)",
    },
  },
  dark: {
    Layout: {
      headerBg: "#070f1f",
      headerColor: "#f4f7ff",
      siderBg: "#061024",
      bodyBg: "#050e1f",
      triggerBg: "#0f2242",
      triggerColor: "#96b5ff",
    },
    Menu: {
      itemColor: "#99a8d3",
      itemSelectedColor: "#ffffff",
      itemSelectedBg: "rgba(24, 144, 255, 0.2)",
      itemHoverColor: "#ffffff",
    },
    Card: {
      colorBgContainer: "#0f1d3b",
      borderRadiusLG: 16,
      paddingLG: 20,
    },
    Table: {
      borderColor: "rgba(255,255,255,0.05)",
      headerBg: "#162853",
      headerColor: "#d5e1ff",
      rowHoverBg: "rgba(24,144,255,0.08)",
    },
  },
};

export const getTheme = (mode: ThemeMode): ThemeConfig => ({
  token: {
    ...sharedTokens,
    ...modeTokens[mode],
  },
  components: {
    ...baseComponents,
    ...modeComponents[mode],
  },
});
